import { ApplicationStatus, VerifyMethodType } from "shared/types";
import { AdditionalData } from "../../types/verified/bank-statment";

const DECISIONED_OR_FROZEN_APPLICATION_STATUSES: ApplicationStatus[] = [
  ApplicationStatus.Approved,
  ApplicationStatus.ConditionalApproved,
  ApplicationStatus.Rejected,
  ApplicationStatus.Canceled
];

const DECISIONED_APPLICATION_REASON_PER_STATUS: Partial<
  Record<ApplicationStatus, string>
> = {
  [ApplicationStatus.Approved]: "approved",
  [ApplicationStatus.ConditionalApproved]: "approved with conditions",
  [ApplicationStatus.Rejected]: "rejected",
  [ApplicationStatus.Canceled]: "canceled"
};

const getGenericRejectionChecks = (
  additionalData: AdditionalData | null,
  documentType: VerifyMethodType
) => [
  {
    condition: additionalData?.type_mismatch,
    reasonCode: {
      code: "",
      confidence: "HIGH",
      description: `The document is not a ${documentType?.replaceAll("_", " ")}`
    }
  },
  {
    condition: additionalData?.is_fraudulent,
    reasonCode: {
      code: "",
      confidence: "HIGH",
      description: "Fraudulent activity detected"
    }
  },
  {
    condition: additionalData?.high_risk_level,
    reasonCode: {
      code: "",
      confidence: "HIGH",
      description: "High risk level"
    }
  },
  {
    condition: additionalData?.low_trust_score,
    reasonCode: {
      code: "",
      confidence: "HIGH",
      description: "Low trust score"
    }
  },
  {
    condition: additionalData?.low_quality_score,
    reasonCode: {
      code: "",
      confidence: "HIGH",
      description: "The file is of poor quality"
    }
  },
  {
    condition: additionalData?.is_name_present === false,
    reasonCode: {
      code: "",
      confidence: "HIGH",
      description: "No name present on document"
    }
  }
];

// For any document verified through Inscribe
export const getGenericReasonCodes = (
  additionalData: AdditionalData,
  documentType: VerifyMethodType
) => {
  const rejectionChecks = getGenericRejectionChecks(
    additionalData,
    documentType
  );

  const reasonCodes = rejectionChecks
    .filter((check) => check.condition)
    .map((check) => check.reasonCode);

  return reasonCodes;
};

export const getActionsDisabledReason = ({
  hasInProgressReverify,
  hasFailedReverify,
  isIncomeIncomplete,
  applicationStatus
}: {
  hasInProgressReverify: boolean;
  hasFailedReverify: boolean;
  isIncomeIncomplete: boolean;
  applicationStatus?: ApplicationStatus;
}): {
  areActionsDisabled: boolean;
  editDisableReason: string;
  reverifyDisableReason: string;
} => {
  const isApplicationDecisionedOrFrozen = applicationStatus
    ? DECISIONED_OR_FROZEN_APPLICATION_STATUSES.includes(applicationStatus)
    : false;
  const areActionsDisabled =
    isIncomeIncomplete ||
    hasInProgressReverify ||
    hasFailedReverify ||
    isApplicationDecisionedOrFrozen;

  const buildReasonMessage = (action: "edit" | "re-verify") => {
    if (isApplicationDecisionedOrFrozen) {
      const verb =
        DECISIONED_APPLICATION_REASON_PER_STATUS[
          applicationStatus as ApplicationStatus
        ] || "in a final state";
      return `Cannot ${action} after the application is ${verb}`;
    }
    if (hasInProgressReverify)
      return `Cannot ${action} while a re-verification is in progress`;
    if (hasFailedReverify)
      return `Cannot ${action} while a re-verification has failed`;
    if (isIncomeIncomplete)
      return `Cannot ${action} income until it is complete`;
    return "";
  };

  return {
    areActionsDisabled,
    editDisableReason: buildReasonMessage("edit"),
    reverifyDisableReason: buildReasonMessage("re-verify")
  };
};
